import { motion } from "framer-motion";
import { ShieldCheck, Clock, PhoneCall, Truck, UserCheck, ArrowLeftRight } from "lucide-react";
import { Eyebrow, MaskLines } from "./Reveal";
import { useLang } from "@/lib/i18n";

const TILES = [
  { k: "safe", icon: ShieldCheck, img: "/img/trailer-straps.jpg", pos: "70% 60%", cls: "lg:col-span-2 lg:row-span-2", big: true },
  { k: "time", icon: Clock, img: "/img/iveco-grey.jpg", pos: "35% 60%" },
  { k: "contact", icon: PhoneCall, img: "/img/trailer-audi.jpg", pos: "60% 60%" },
  { k: "fleet", icon: Truck, img: "/img/actros-full.jpg", pos: "50% 60%", cls: "lg:col-span-2" },
  { k: "drivers", icon: UserCheck, img: "/img/driver.jpg", pos: "50% 12%", cls: "lg:col-span-2" },
  { k: "ie", icon: ArrowLeftRight, img: "/img/actros-port.jpg", pos: "50% 60%", cls: "lg:col-span-2" },
];

const Tile = ({ tile, item, i }) => {
  const Icon = tile.icon;
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
  };
  return (
    <motion.article
      data-testid={`bento-tile-${tile.k}`}
      onMouseMove={onMove}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative flex min-h-[260px] overflow-hidden rounded-[22px] border border-white/10 bg-[#14171E] ${tile.cls || ""} ${tile.big ? "min-h-[420px]" : ""}`}
    >
      <div className="absolute inset-0 overflow-hidden">
        <img src={tile.img} alt={item.t} loading="lazy" style={{ objectPosition: tile.pos }} className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#090A0C] via-[#090A0C]/60 to-[#090A0C]/10" />
      </div>
      <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" style={{ background: "radial-gradient(380px circle at var(--x) var(--y), rgba(255,255,255,0.10), transparent 60%)" }} />
      <div className="relative z-10 flex w-full flex-col justify-between p-7 sm:p-8">
        <span className="grid h-12 w-12 place-items-center rounded-full border border-white/20 bg-black/40 text-white backdrop-blur-md">
          <Icon className="h-5 w-5" />
        </span>
        <div className="mt-10">
          <h3 className={`font-display font-extrabold uppercase italic leading-none ${tile.big ? "text-4xl sm:text-6xl" : "text-3xl"}`}>{item.t}</h3>
          <p className={`mt-3 max-w-md text-sm leading-relaxed sm:text-base text-zinc-200`}>{item.d}</p>
        </div>
      </div>
    </motion.article>
  );
};

export default function Bento() {
  const { t, lang } = useLang();
  return (
    <section id="services" data-testid="services-section" className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 sm:py-28">
      <Eyebrow>{t.services.eyebrow}</Eyebrow>
      <h2 className="mb-14 font-display text-[2.5rem] font-black uppercase italic leading-[0.9] sm:text-7xl">
        <MaskLines key={lang} lines={t.services.title} onView lineClass="[&]:text-white" />
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:auto-rows-[260px] lg:grid-cols-4">
        {TILES.map((tile, i) => (
          <Tile key={tile.k} tile={tile} item={t.services.items[tile.k]} i={i} />
        ))}
      </div>
    </section>
  );
}
